import { API_URL, isMocksEnabled } from "./helpers";
import { getActorTasksMock } from "../__mocks__/usersSummary";
import {
  ActorInfoApi,
  ActorSummary,
  Tasks,
  UpdateActorTaskInput,
} from "./Users";

import { useAppContext } from "../AppProviders";
import { useMutation, useQuery } from "react-query";
import { useEffect, useMemo } from "react";
import { useFamaFlags } from "./FamaFlags";
import { usePermissionValidator } from "./UserPermissionValidators";

export function useCreateActor() {
  const { requestApi } = useAppContext();

  const createActor = async (userInput: ActorInfoApi): Promise<any> => {
    return requestApi
      .post(`${API_URL}/actor_info`, userInput)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (userInput: ActorInfoApi) => createActor(userInput),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useCreateActorById() {
  const { requestApi } = useAppContext();

  const getActorById = async (id: string): Promise<ActorInfoApi> => {
    return requestApi.get(`${API_URL}/actor_info/${id}`).then((response) => {
      return response.data;
    });
  };

  const mutationResult = useMutation((id: string) => getActorById(id), {});

  return {
    ...mutationResult,
  };
}

export function useGetActorInfo() {
  const { requestApi } = useAppContext();

  const getActor = async (): Promise<ActorInfoApi> => {
    return requestApi.get(`${API_URL}/actor_info`).then((response) => {
      return response.data;
    });
  };

  const mutationResult = useMutation(() => getActor(), {});

  return {
    ...mutationResult,
  };
}

export function useActorInfoQuery(enabled: boolean = true) {
  const { requestApi } = useAppContext();

  return useQuery<ActorInfoApi>(
    ["actorInfo"],
    async () => {
      const response = await requestApi.get(`${API_URL}/actor_info`);
      return response.data;
    },
    {
      enabled,
      staleTime: 5 * 60 * 1000, // 5 minutes - actor info rarely changes during a session
      cacheTime: 10 * 60 * 1000,
    }
  );
}

export function useGetActorSummary() {
  const { requestApi } = useAppContext();

  const getActorSummary = async (): Promise<ActorSummary[]> => {
    if (isMocksEnabled()) {
      return await getActorSummary();
    }
    return requestApi.get(`${API_URL}/actor/summary`).then((response) => {
      return response.data;
    });
  };

  const mutationResult = useMutation(() => getActorSummary(), {});

  return {
    ...mutationResult,
  };
}

export function useGetActorTasks() {
  const { requestApi } = useAppContext();

  const getActorTasks = async (): Promise<Tasks[]> => {
    if (isMocksEnabled()) {
      return await getActorTasksMock();
    }
    return requestApi.get(`${API_URL}/actordal/tasks`).then((response) => {
      return response.data?.sort(
        (prev: Tasks, after: Tasks) => prev.order - after.order
      );
    });
  };

  const mutationResult = useMutation(() => getActorTasks(), {});

  return {
    ...mutationResult,
  };
}

export function useUpdateActorTask() {
  const { requestApi } = useAppContext();

  const updateActorTasks = async (
    input: UpdateActorTaskInput[]
  ): Promise<Tasks[]> => {
    return requestApi
      .post(`${API_URL}/actordal/actor_tasks_update`, input)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: UpdateActorTaskInput[]) => updateActorTasks(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

/** Canonical rule for queue-only (e.g. enter_queue redirect, nav). */
export function computeHasOnlyQueue(
  actorInfo: ActorInfoApi | undefined,
  hasTasksOnlyQueue: boolean,
  canManageReports: boolean
): boolean {
  if (!actorInfo) return false;
  return Boolean(
    actorInfo.has_task && hasTasksOnlyQueue && canManageReports
  );
}

export function useHasOnlyQueueFromActorInfo(
  actorInfo: ActorInfoApi | undefined
): boolean {
  const { hasTasksOnlyQueue } = useFamaFlags();
  const { canManageReports } = usePermissionValidator();
  return useMemo(
    () =>
      computeHasOnlyQueue(actorInfo, hasTasksOnlyQueue, canManageReports),
    [actorInfo, hasTasksOnlyQueue, canManageReports]
  );
}

export function useGetHasTasksOnlyQueue() {
  const {
    mutate: getActorInfo,
    data: actorInfo,
    isLoading,
  } = useGetActorInfo();

  useEffect(() => {
    getActorInfo();
  }, [getActorInfo]);

  const hasOnlyQueue = useHasOnlyQueueFromActorInfo(actorInfo);

  return { hasOnlyQueue, isLoadingHasOnlyQueue: isLoading || !actorInfo };
}
